'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import Dashboard from './components/Dashboard';
import { cn } from '@/lib/utils';

export default function Home() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const pageTitles: Record<string, { title: string; subtitle?: string }> = {
    dashboard: { title: '仪表板', subtitle: '网络设备运行概览' },
    devices: { title: '设备管理', subtitle: '管理所有网络设备' },
    monitoring: { title: '实时监控', subtitle: '实时性能监控' },
    alerts: { title: '告警中心', subtitle: '设备告警管理' },
    topology: { title: '网络拓扑', subtitle: '可视化网络结构' },
    logs: { title: '系统日志', subtitle: '操作与系统日志' },
    users: { title: '用户管理', subtitle: '管理系统用户' },
    settings: { title: '系统设置', subtitle: '系统配置' },
  };

  const currentPage = pageTitles[activeTab] || pageTitles.dashboard;

  return (
    <div className="min-h-screen bg-[var(--background)]">
      {/* 侧边栏 */}
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />

      {/* 主内容区 */}
      <main className={cn(
        "transition-all duration-300 min-h-screen",
        sidebarCollapsed ? "ml-[72px]" : "ml-64"
      )}>
        {/* 顶部导航 */}
        <TopBar 
          title={currentPage.title}
          subtitle={currentPage.subtitle}
        />

        {/* 页面内容 */}
        <div className="p-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {activeTab === 'dashboard' && <Dashboard />}
              
              {activeTab === 'devices' && (
                <div className="bg-white rounded-xl border border-[var(--border)] p-12 text-center">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[var(--secondary)] flex items-center justify-center">
                    <svg className="w-10 h-10 text-[var(--muted-foreground)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-[var(--foreground)] mb-2">设备管理页面</h3>
                  <p className="text-[var(--muted-foreground)]">此功能正在开发中，敬请期待...</p>
                </div>
              )}
              
              {activeTab === 'monitoring' && (
                <div className="bg-white rounded-xl border border-[var(--border)] p-12 text-center">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[var(--secondary)] flex items-center justify-center">
                    <svg className="w-10 h-10 text-[var(--muted-foreground)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-[var(--foreground)] mb-2">实时监控页面</h3>
                  <p className="text-[var(--muted-foreground)]">此功能正在开发中，敬请期待...</p>
                </div>
              )}
              
              {activeTab === 'alerts' && (
                <div className="bg-white rounded-xl border border-[var(--border)] p-12 text-center">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[var(--secondary)] flex items-center justify-center">
                    <svg className="w-10 h-10 text-[var(--muted-foreground)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-[var(--foreground)] mb-2">告警中心页面</h3>
                  <p className="text-[var(--muted-foreground)]">此功能正在开发中，敬请期待...</p>
                </div>
              )}
              
              {activeTab === 'topology' && (
                <div className="bg-white rounded-xl border border-[var(--border)] p-6">
                  <h3 className="text-lg font-medium text-[var(--foreground)] mb-4">网络拓扑</h3>
                  <p className="text-[var(--muted-foreground)] mb-4">详细网络拓扑视图功能正在开发中...</p>
                </div>
              )}
              
              {activeTab === 'logs' && (
                <div className="bg-white rounded-xl border border-[var(--border)] p-12 text-center">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[var(--secondary)] flex items-center justify-center">
                    <svg className="w-10 h-10 text-[var(--muted-foreground)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-[var(--foreground)] mb-2">系统日志页面</h3>
                  <p className="text-[var(--muted-foreground)]">此功能正在开发中，敬请期待...</p>
                </div>
              )}
              
              {activeTab === 'users' && (
                <div className="bg-white rounded-xl border border-[var(--border)] p-12 text-center">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[var(--secondary)] flex items-center justify-center">
                    <svg className="w-10 h-10 text-[var(--muted-foreground)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-[var(--foreground)] mb-2">用户管理页面</h3>
                  <p className="text-[var(--muted-foreground)]">此功能正在开发中，敬请期待...</p>
                </div>
              )}
              
              {activeTab === 'settings' && (
                <div className="bg-white rounded-xl border border-[var(--border)] p-12 text-center">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[var(--secondary)] flex items-center justify-center">
                    <svg className="w-10 h-10 text-[var(--muted-foreground)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-[var(--foreground)] mb-2">系统设置页面</h3>
                  <p className="text-[var(--muted-foreground)]">此功能正在开发中，敬请期待...</p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
